import type { Lang } from "../translations";

/**
 * Source-of-truth graph for indexable URLs on the marketing site.
 *
 * Every entry here is a real, indexable page (or a planned one with `published: false`,
 * which keeps the graph self-documenting while letting SEO-only pages roll out
 * one PR at a time without footer churn). Footer columns, related-block
 * components, and any future "internal-link audits" all read from this single
 * structure — no duplicated link lists in templates.
 *
 * Per-node `label` and `blurb` are localised; `path` is the language-agnostic
 * route under the language root (callers prepend `/pt` or `/es` for the
 * non-EN language trees via `useLp()`).
 */

export type InternalLinkKind = "comparison" | "useCase" | "blog" | "core";

export interface InternalLinkNode {
  /** Stable id used for related-block dedup and tests. */
  id: string;
  /** Historical / route-ish IDs accepted for related-block dedup. */
  aliases?: readonly string[];
  kind: InternalLinkKind;
  /** Path WITHOUT the language prefix (e.g. "/compare/otter-vs-minuta"). */
  path: string;
  /** Localised short label — used for footer columns and related-block titles. */
  label: Record<Lang, string>;
  /** Optional one-line summary used by related-block cards. */
  blurb?: Record<Lang, string>;
  /**
   * Whether the route is currently live in the SPA. Unpublished entries are
   * excluded from footer/related output but stay in the graph so MINA-49 /
   * MINA-50 (and follow-ups) can flip a single flag when their routes ship.
   */
  published: boolean;
}

const NODES: InternalLinkNode[] = [
  // -----------------------------------------------------------------------
  // Comparisons
  // -----------------------------------------------------------------------
  {
    id: "compare-otter",
    kind: "comparison",
    path: "/compare/otter-vs-minuta",
    label: {
      en: "Otter.ai vs. Minuta",
      pt: "Otter.ai vs. Minuta",
      es: "Otter.ai vs. Minuta",
    },
    blurb: {
      en: "Cloud bot vs. local copilot — pick the right one honestly.",
      pt: "Bot na nuvem vs. copiloto local — escolha o certo, sem marketing.",
      es: "Bot en la nube vs. copiloto local — elige el correcto, sin marketing.",
    },
    published: true,
  },
  {
    id: "compare-fireflies",
    kind: "comparison",
    path: "/compare/fireflies-vs-minuta",
    label: {
      en: "Fireflies vs. Minuta",
      pt: "Fireflies vs. Minuta",
      es: "Fireflies vs. Minuta",
    },
    blurb: {
      en: "Bot-based meeting notes vs. invisible, on-device copilot.",
      pt: "Notas com bot na call vs. copiloto invisível no seu computador.",
      es: "Notas con bot en la call vs. copiloto invisible en tu computadora.",
    },
    published: true,
  },
  {
    id: "compare-granola",
    kind: "comparison",
    path: "/compare/granola-vs-minuta",
    label: {
      en: "Granola vs. Minuta",
      pt: "Granola vs. Minuta",
      es: "Granola vs. Minuta",
    },
    blurb: {
      en: "Cloud-AI meeting notes vs. fully local, real-time copilot.",
      pt: "Notas com IA na nuvem vs. copiloto local em tempo real.",
      es: "Notas con IA en la nube vs. copiloto local en tiempo real.",
    },
    published: true,
  },
  {
    id: "compare-zoom-ai-companion",
    kind: "comparison",
    path: "/compare/zoom-ai-companion-vs-minuta",
    label: {
      en: "Zoom AI Companion vs. Minuta",
      pt: "Zoom AI Companion vs. Minuta",
      es: "Zoom AI Companion vs. Minuta",
    },
    blurb: {
      en: "Zoom-only summaries vs. cross-platform on-device copilot.",
      pt: "Resumos só do Zoom vs. copiloto local em qualquer plataforma.",
      es: "Resúmenes solo de Zoom vs. copiloto local en cualquier plataforma.",
    },
    published: true,
  },
  {
    id: "compare-tactiq",
    kind: "comparison",
    path: "/compare/tactiq-vs-minuta",
    label: {
      en: "Tactiq vs. Minuta",
      pt: "Tactiq vs. Minuta",
      es: "Tactiq vs. Minuta",
    },
    blurb: {
      en: "Browser-tab transcripts vs. on-device copilot for English calls.",
      pt: "Transcrição na aba do navegador vs. copiloto local em call em inglês.",
      es: "Transcripción en pestaña vs. copiloto local en calls en inglés.",
    },
    published: true,
  },
  {
    id: "compare-tldv",
    kind: "comparison",
    path: "/compare/tldv-vs-minuta",
    label: {
      en: "tl;dv vs. Minuta",
      pt: "tl;dv vs. Minuta",
      es: "tl;dv vs. Minuta",
    },
    blurb: {
      en: "Bot-recorded video clips vs. real-time copilot on your computer.",
      pt: "Bot que grava clipes de vídeo vs. copiloto em tempo real no seu computador.",
      es: "Bot que graba clips de vídeo vs. copiloto en tiempo real en tu computadora.",
    },
    published: true,
  },
  {
    id: "compare-krisp",
    kind: "comparison",
    path: "/compare/krisp-vs-minuta",
    label: {
      en: "Krisp vs. Minuta",
      pt: "Krisp vs. Minuta",
      es: "Krisp vs. Minuta",
    },
    blurb: {
      en: "Krisp's noise-cancel + notes vs. Minuta's real-time copilot.",
      pt: "Notas + noise-cancel da Krisp vs. copiloto em tempo real do Minuta.",
      es: "Notas + noise-cancel de Krisp vs. copiloto en tiempo real de Minuta.",
    },
    published: true,
  },

  // -----------------------------------------------------------------------
  // Use cases
  // -----------------------------------------------------------------------
  {
    id: "use-case-ai-meeting-copilot",
    kind: "useCase",
    path: "/use-cases/ai-meeting-copilot",
    label: {
      en: "AI meeting copilot",
      pt: "Copiloto de IA para reunião",
      es: "Copiloto de IA para reuniones",
    },
    blurb: {
      en: "The central hub for answering live during difficult English calls.",
      pt: "O hub central para responder ao vivo em calls difíceis em inglês.",
      es: "El hub central para responder en vivo en calls difíciles en inglés.",
    },
    published: true,
  },
  {
    id: "use-case-sales-engineer-discovery",
    kind: "useCase",
    path: "/use-cases/sales-engineer-discovery-call",
    label: {
      en: "Sales engineer · discovery call",
      pt: "Sales engineer · call de discovery",
      es: "Sales engineer · call de discovery",
    },
    blurb: {
      en: "Stay sharp on technical questions when the call is in English.",
      pt: "Não trava na pergunta técnica quando a call é em inglês.",
      es: "No te bloquees en la pregunta técnica cuando la call es en inglés.",
    },
    published: true,
  },
  {
    id: "use-case-recruiting-screening",
    kind: "useCase",
    path: "/use-cases/recruiting-screening-call",
    label: {
      en: "Recruiting · screening call",
      pt: "Recrutamento · screening call",
      es: "Reclutamiento · screening call",
    },
    blurb: {
      en: "Run an English screening call without freezing on the live answer.",
      pt: "Conduza screening em inglês sem travar na resposta ao vivo.",
      es: "Conduce un screening en inglés sin bloquearte en la respuesta en vivo.",
    },
    published: false,
  },
  {
    id: "use-case-legal-deposition",
    aliases: ["use-case-legal-deposition-on-device"],
    kind: "useCase",
    path: "/use-cases/legal-deposition-meeting-copilot",
    label: {
      en: "Legal · deposition meeting copilot",
      pt: "Jurídico · copiloto para depoimentos",
      es: "Legal · copiloto para deposiciones",
    },
    blurb: {
      en: "Live English answer copilot for depositions — no bot in the call, auditable cloud chain.",
      pt: "Copiloto de resposta ao vivo para depoimentos em inglês — sem robô, cadeia cloud documentada.",
      es: "Copiloto de respuesta en vivo para deposiciones en inglés — sin bot, cadena cloud documentada.",
    },
    published: false,
  },
  {
    id: "use-case-customer-success-renewal",
    kind: "useCase",
    path: "/use-cases/customer-success-renewal-call",
    label: {
      en: "Customer success · renewal call",
      pt: "Customer success · call de renovação",
      es: "Customer success · call de renovación",
    },
    blurb: {
      en: "Never miss a renewal signal in a fast-moving English account call.",
      pt: "Não perde sinal de renovação em call em inglês cheia de detalhe.",
      es: "No pierdas señal de renovación en una call en inglés con mucho detalle.",
    },
    published: false,
  },
  {
    id: "use-case-freelancer-discovery",
    aliases: ["use-case-freelancer-discovery-call"],
    kind: "useCase",
    path: "/use-cases/freelancer-discovery-call",
    label: {
      en: "Freelancer · first US discovery call",
      pt: "Freelancer · primeira discovery call com cliente US",
      es: "Freelancer · primera discovery call con cliente US",
    },
    blurb: {
      en: "Land your first US client without freezing on the day-rate question.",
      pt: "Fecha o primeiro cliente US sem travar quando perguntam seu day rate.",
      es: "Cierra tu primer cliente US sin bloquearte cuando preguntan tu day rate.",
    },
    published: true,
  },
  {
    id: "use-case-tech-lead-one-on-one",
    kind: "useCase",
    path: "/use-cases/tech-lead-one-on-one",
    label: {
      en: "Tech lead · 1-on-1 in English",
      pt: "Tech lead · 1-on-1 em inglês",
      es: "Tech lead · 1-on-1 en inglés",
    },
    blurb: {
      en: "Calibrated feedback in English when the words have to land just right.",
      pt: "Feedback calibrado em inglês quando a palavra certa importa de verdade.",
      es: "Feedback calibrado en inglés cuando la palabra correcta importa de verdad.",
    },
    published: true,
  },
  {
    id: "use-case-zoom-realtime-translation",
    kind: "useCase",
    path: "/use-cases/zoom-realtime-translation",
    label: {
      en: "Zoom · real-time EN ↔ PT/ES translation",
      pt: "Zoom · tradução EN ↔ PT/ES em tempo real",
      es: "Zoom · traducción EN ↔ PT/ES en tiempo real",
    },
    blurb: {
      en: "Side-by-side translation during Zoom, Teams, or Meet — fully on-device.",
      pt: "Tradução lado a lado durante Zoom, Teams ou Meet — local no computador.",
      es: "Traducción lado a lado durante Zoom, Teams o Meet — local en la computadora.",
    },
    published: true,
  },
  {
    id: "use-case-zoom-auto-meeting-notes",
    kind: "useCase",
    path: "/use-cases/zoom-auto-meeting-notes",
    label: {
      en: "Zoom · auto meeting notes",
      pt: "Zoom · ata automática",
      es: "Zoom · acta automática",
    },
    blurb: {
      en: "Structured TL;DR + action items after every call. No bot, no platform lock-in.",
      pt: "TL;DR estruturado + action items depois de cada call. Sem bot, sem lock-in de plataforma.",
      es: "TL;DR estructurado + action items después de cada call. Sin bot, sin lock-in de plataforma.",
    },
    published: true,
  },
  {
    id: "use-case-independent-tech-consultant-call",
    kind: "useCase",
    path: "/use-cases/independent-tech-consultant-call",
    label: {
      en: "Independent consultant · scoping call",
      pt: "Consultor PJ · scoping call internacional",
      es: "Consultor independiente · scoping call internacional",
    },
    blurb: {
      en: "Sound senior on the architecture pressure-test when 5-figures is on the line.",
      pt: "Soa senior no pressure-test de arquitetura quando o engagement vale 5 dígitos.",
      es: "Suena senior en el pressure-test de arquitectura cuando el engagement vale 5 dígitos.",
    },
    published: true,
  },
  {
    id: "use-case-private-meeting-transcription",
    aliases: ["use-case-private-on-device", "use-case-private-on-device-meeting-transcription"],
    kind: "useCase",
    path: "/use-cases/private-meeting-transcription",
    label: {
      en: "Private · meeting transcription",
      pt: "Privado · transcrição de reunião",
      es: "Privado · transcripción de reunión",
    },
    blurb: {
      en: "No bot, narrow cloud chain, transcripts on your machine. See the full data-flow on our Trust page.",
      pt: "Sem bot, cadeia cloud revisada, transcrições na sua máquina. Veja o fluxo de dados na página de Trust.",
      es: "Sin bot, cadena cloud revisada, transcripciones en tu máquina. Ve el flujo de datos en nuestra página de Trust.",
    },
    published: false,
  },
  {
    id: "use-case-no-plugin",
    aliases: ["use-case-no-plugin-meeting-transcription"],
    kind: "useCase",
    path: "/use-cases/no-plugin-meeting-transcription",
    label: {
      en: "No plugin · no bot · no extension",
      pt: "Sem plugin · sem bot · sem extensão",
      es: "Sin plugin · sin bot · sin extensión",
    },
    blurb: {
      en: "Signed desktop app — no Chrome extension, no calendar OAuth, no bot in the call.",
      pt: "App desktop assinado — sem extensão Chrome, sem OAuth de calendário, sem bot na call.",
      es: "App desktop firmada — sin extensión Chrome, sin OAuth de calendario, sin bot en la call.",
    },
    published: true,
  },

  // -----------------------------------------------------------------------
  // Core / always-indexable
  // -----------------------------------------------------------------------
  {
    id: "blog",
    kind: "blog",
    path: "/blog",
    label: { en: "Blog", pt: "Blog", es: "Blog" },
    blurb: {
      en: "Field notes on local-first AI, meetings, and the Minuta build.",
      pt: "Notas de campo sobre IA local, reuniões e a construção do Minuta.",
      es: "Notas de campo sobre IA local, reuniones y cómo construimos Minuta.",
    },
    published: true,
  },
  {
    id: "downloads",
    kind: "core",
    path: "/downloads",
    label: { en: "Download Minuta", pt: "Baixar o Minuta", es: "Descargar Minuta" },
    published: true,
  },
  {
    id: "trust",
    kind: "core",
    path: "/trust",
    label: { en: "Trust", pt: "Seus dados", es: "Tus datos" },
    published: true,
  },
  {
    id: "privacy",
    kind: "core",
    path: "/privacy",
    label: { en: "Privacy", pt: "Privacidade", es: "Privacidad" },
    published: true,
  },
  {
    id: "terms",
    kind: "core",
    path: "/terms",
    label: { en: "Terms", pt: "Termos", es: "Términos" },
    published: true,
  },
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const USE_CASE_PRIORITY_IDS = [
  "use-case-ai-meeting-copilot",
  "use-case-private-meeting-transcription",
  "use-case-no-plugin",
  "use-case-legal-deposition",
] as const;

const useCasePriority = new Map<string, number>(
  USE_CASE_PRIORITY_IDS.map((id, index) => [id, index]),
);

const CLOUD_SUPPRESSED_IDS = new Set([
  "use-case-private-meeting-transcription",
  "use-case-legal-deposition",
]);

/** Returns nodes with `published: true` only — the data callers usually want. */
export function listInternalLinks(): InternalLinkNode[] {
  return NODES.filter((n) => n.published && !isSuppressedByCloudPositioning(n));
}

/** All published nodes of a given kind (e.g. all live comparison pages). */
export function listByKind(kind: InternalLinkKind): InternalLinkNode[] {
  const nodes = listInternalLinks().filter((n) => n.kind === kind);
  if (kind !== "useCase") return nodes;

  return [...nodes].sort((a, b) => {
    const aPriority = useCasePriority.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const bPriority = useCasePriority.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    return aPriority - bPriority;
  });
}

/** Stable lookup by id; returns even unpublished nodes (for callers that own the route). */
export function getNode(id: string): InternalLinkNode | undefined {
  return NODES.find((n) => n.id === id);
}

/**
 * Returns up to `max` related published nodes of the given `kind`, excluding
 * the one matching `currentId`. Order follows the declaration order in
 * `NODES`, which intentionally puts the highest-priority pages first.
 */
export function getRelated(
  currentId: string,
  kind: InternalLinkKind,
  max = 3,
): InternalLinkNode[] {
  return listByKind(kind)
    .filter((n) => !matchesNode(n, currentId))
    .slice(0, max);
}

function matchesNode(node: InternalLinkNode, id: string): boolean {
  return node.id === id || node.aliases?.includes(id) === true;
}

function isSuppressedByCloudPositioning(node: InternalLinkNode): boolean {
  return CLOUD_SUPPRESSED_IDS.has(node.id);
}

export interface FooterColumns {
  comparisons: InternalLinkNode[];
  useCases: InternalLinkNode[];
  resources: InternalLinkNode[];
}

const FOOTER_RESOURCE_IDS = ["blog", "downloads", "trust", "privacy", "terms"] as const;

/**
 * Footer payload — comparison + use-case + resources columns, all drawn from
 * the same graph. The Footer renders whatever is published; the rest stays
 * hidden until its sister ticket flips `published: true`.
 */
export function getFooterColumns(): FooterColumns {
  return {
    comparisons: listByKind("comparison"),
    useCases: listByKind("useCase"),
    resources: FOOTER_RESOURCE_IDS.map((id) => getNode(id)).filter(
      (n): n is InternalLinkNode => !!n && n.published,
    ),
  };
}

/** Total count of published internal links exposed in the footer. Used by tests. */
export function countFooterInternalLinks(): number {
  const { comparisons, useCases, resources } = getFooterColumns();
  return comparisons.length + useCases.length + resources.length;
}
